import { useAppTranslation } from 'src/i18n/hooks';
import { LoadingOutlined } from '@ant-design/icons';
import { message, Modal, Progress } from 'antd';
import { TabletSmartphone } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getAccessToken } from 'src/helpers/storage';
import { useAppSelector } from 'src/store';
import { useAccount, useChainId, useSignTypedData } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';

const ShowQRButton = () => {
  const theme = useAppSelector((state) => state.theme);
  const { t } = useAppTranslation(['common']);
  const chainId = useChainId();
  const [openModal, setOpenModal] = useState(false);
  const [openApprovingModal, setOpenApprovingModal] = useState(false);

  const [_, setRenderTrigger] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const intervalRef = useRef<NodeJS.Timer>();
  const percentageRef = useRef<number>(0);
  const { address: userAddress } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const { openConnectModal } = useConnectModal();

  const url = useRef<string>(`${getAccessToken()}`);
  const handleMouseDown = () => {
    const interval = setInterval(() => {
      setRenderTrigger((pre) => pre + 1);
      percentageRef.current = percentageRef.current + 1;
    }, 10);
    intervalRef.current = interval;
  };

  const handleMouseUp = () => {
    if (percentageRef.current < 100) {
      percentageRef.current = 0;
      setRenderTrigger((pre) => pre + 1);
    } else {
      setIsDone(true);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const signMessageData = useMemo(() => {
    const domain = {
      name: 'VdexSignTransaction',
      version: '1',
      chainId,
      verifyingContract: '0x0000000000000000000000000000000000000000',
    } as any;

    const types = {
      EIP712Domain: [
        {
          name: 'name',
          type: 'string',
        },
        {
          name: 'version',
          type: 'string',
        },
        {
          name: 'chainId',
          type: 'uint256',
        },
        {
          name: 'verifyingContract',
          type: 'address',
        },
      ],
      'VdexTransaction:ApproveAgent': [
        {
          name: 'chain',
          type: 'string',
        },
        {
          name: 'agentName',
          type: 'string',
        },
        {
          name: 'nonce',
          type: 'uint64',
        },
      ],
    };

    const message = {
      chain: 'Mainnet',
      agentName: 'Mobile QR',
      nonce: new Date().getTime(),
      type: 'approveAgent',
    };
    return {
      message,
      domain,
      types,
    };
  }, []);

  const handleOpenQRModal = async () => {
    try {
      if (!userAddress) {
        openConnectModal?.();
        return;
      }
      setOpenApprovingModal(true);
      await signTypedDataAsync({
        domain: signMessageData.domain,
        types: signMessageData.types,
        message: signMessageData.message,
        primaryType: 'VdexTransaction:ApproveAgent',
      });
      setOpenModal(true);
    } catch (error: any) {
      if (error.code == -32603) {
        message.error(t('wallet.wrongNetwork'));
      }
    }
    setOpenApprovingModal(false);
  };

  return (
    <div className="flex item-center">
      <button onClick={handleOpenQRModal}>
        <TabletSmartphone size={18} className="dark:text-white" />
      </button>
      <Modal
        open={openModal}
        onCancel={() => {
          percentageRef.current = 0;
          setOpenModal(false);
          setIsDone(false);
        }}
        footer={null}
        centered
        destroyOnClose
        className={`z-10 font-poppins ${theme}`}
      >
        <div className="flex flex-col items-center mt-[20px] gap-[10px]">
          <div className="text-[18px]">{t('wallet.linkMobileDevice')}</div>
          <div className="text-[12px] text-[#9aa3a4] text-center">
            {t('wallet.qrModalDescription')}
          </div>
          {isDone ? (
            <div className="p-[24px] bg-[white] w-[348px] h-[348px]">
              <QRCodeSVG value={url.current} width={300} height={300} marginSize={0} />,
            </div>
          ) : (
            <button
              className="w-[100%] bg-[#7306c7] py-[10px] flex items-center gap-[5px] justify-center rounded-[8px]"
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
            >
              <Progress
                trailColor="#7979ff"
                strokeColor={'white'}
                showInfo={false}
                size={20}
                type="circle"
                percent={percentageRef.current}
              />
              <span className="text-white">{t('wallet.holdToReveal')}</span>
            </button>
          )}
          <div className="text-[#ed7088] border-[#ed7088] border-[1px] rounded-[12px] bg-[#34242e] px-[20px] py-[10px]">
            <span>{t('wallet.qrModalWaring')}</span>
          </div>
        </div>
      </Modal>
      <Modal
        open={openApprovingModal}
        onCancel={() => setOpenApprovingModal(false)}
        footer={null}
        centered
        className="z-10 font-poppins"
      >
        <div className="flex flex-col items-center gap-[20px]">
          <div>
            <LoadingOutlined
              style={{
                fontSize: 40,
              }}
            />
          </div>
          <span className="text-[16px] ">{t('wallet.qrModalLoadingText')}</span>
        </div>
      </Modal>
    </div>
  );
};

export default ShowQRButton;
